// Config for the 8 service detail pages — original content
import { Layers, Feather, Hammer, Thermometer, Volume2, Wrench, Timer, Truck } from "lucide-react";

export const SERVICE_GROUPS = {
  chapes: {
    label: "Nos chapes",
    basePath: "/chapes",
    intro:
      "Trois techniques de chape à choisir selon le support, la charge admissible et les revêtements prévus. Chacune demande son propre tour de main.",
  },
  isolations: {
    label: "Nos isolations",
    basePath: "/isolations",
    intro:
      "Isoler par le sol, c'est gagner en confort toute l'année et rester conforme aux exigences thermiques et acoustiques en vigueur.",
  },
  solutions: {
    label: "Nos solutions",
    basePath: "/solutions",
    intro:
      "Planning serré, plancher existant compliqué, sol qui doit encaisser de la charge : chaque contrainte a sa réponse technique.",
  },
};

export const SERVICES = [
  {
    slug: "chape-traditionnelle",
    group: "chapes",
    icon: Layers,
    title: "Chape traditionnelle",
    tagline: "Le grand classique du bâtiment.",
    image:
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/ma6i0ldd_20260306_124703.jpg",
    gallery: [
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/ma6i0ldd_20260306_124703.jpg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/q73waqci_image_1777579429306.jpeg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/64dlk4nx_20260304_134950.jpg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/q89b48xp_Snapchat-1467048244.jpg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/20734evn_Snapchat-1332156429.jpg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/od2lzkcm_Snapchat-733142145.jpg",
    ],
    short:
      "Mortier de ciment et sable taloché, pour un sol robuste et durable adapté à tous les revêtements.",
    description:
      "La chape traditionnelle est obtenue par un mélange de ciment, de sable et d'eau, taloché à la main par un chapiste expérimenté. Sa mise en œuvre demande un véritable savoir-faire : préparation du support, dosage précis, contrôle de la planéité. Bien réalisée, elle constitue le support universel pour toutes les finitions et reste la référence en matière de durabilité.",
    sections: [
      {
        title: "Domaines d'application",
        items: [
          "Maisons individuelles neuves",
          "Appartements et logements collectifs",
          "Rénovations lourdes et extensions",
          "Locaux tertiaires et commerciaux",
        ],
      },
      {
        title: "Avantages",
        items: [
          "Excellente résistance mécanique",
          "Compatible avec tous les revêtements (carrelage, parquet, résine, vinyle)",
          "Durée de vie exceptionnelle",
          "Mise en œuvre maîtrisée et fiable",
        ],
      },
      {
        title: "Caractéristiques techniques",
        items: [
          "Épaisseur courante : 4 à 6 cm",
          "Dosage adapté à la résistance recherchée",
          "Pose désolidarisée, adhérente ou flottante selon le projet",
          "Délai de séchage classique avant pose du revêtement",
        ],
      },
    ],
  },
  {
    slug: "chape-allegee",
    group: "chapes",
    icon: Feather,
    title: "Chape allégée",
    tagline: "Le poids en moins, la performance en plus.",
    image:
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/n1nd3e1w_copilot_image_1777624997018.jpeg",
    gallery: [
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/n1nd3e1w_copilot_image_1777624997018.jpeg",
    ],
    short:
      "Chape légère à base de granulats expansés, pensée pour les rénovations et planchers à charge limitée.",
    description:
      "La chape allégée se compose d'un mortier intégrant des granulats légers expansés. Elle réduit significativement le poids sur la structure tout en assurant une excellente planéité et un confort thermique appréciable. C'est la solution privilégiée en rénovation lorsque le plancher existant ne permet pas de supporter une chape classique.",
    sections: [
      {
        title: "Domaines d'application",
        items: [
          "Rénovation d'immeubles anciens",
          "Planchers bois ou solivages",
          "Étages soumis à des contraintes de charge",
          "Surélévations et combles aménagés",
        ],
      },
      {
        title: "Avantages",
        items: [
          "Réduction de la charge sur la structure",
          "Bonne tenue mécanique",
          "Compatibilité avec une isolation thermique sous chape",
          "Mise à niveau facilitée des sols irréguliers",
        ],
      },
      {
        title: "Caractéristiques techniques",
        items: [
          "Densité réduite par rapport à une chape traditionnelle",
          "Épaisseur adaptable selon les contraintes",
          "Compatible avec la plupart des revêtements de sol",
          "Pose intérieure uniquement",
        ],
      },
    ],
  },
  {
    slug: "beton-dur",
    group: "chapes",
    icon: Hammer,
    title: "Béton dur",
    tagline: "Le sol des espaces qui travaillent.",
    image:
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/8scg9ah7_image_1777561239581.jpeg",
    gallery: [
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/8scg9ah7_image_1777561239581.jpeg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/tsr80yi9_Snapchat-719448906.jpg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/rofrxnm5_Snapchat-1794743808.jpg",
    ],
    short:
      "Sols industriels lissés à haute résistance pour ateliers, garages, entrepôts et zones de forte sollicitation.",
    description:
      "Le béton dur est un sol industriel coulé puis lissé mécaniquement, intégrant un durcisseur de surface qui décuple sa résistance à l'usure et aux chocs. Il offre un rendu lisse, anti-poussière et facile d'entretien, idéal pour les locaux soumis à un trafic intense ou au passage de véhicules.",
    sections: [
      {
        title: "Domaines d'application",
        items: [
          "Ateliers, hangars, entrepôts logistiques",
          "Garages, parkings, zones de stationnement",
          "Locaux industriels et de production",
          "Showrooms, halls techniques",
        ],
      },
      {
        title: "Avantages",
        items: [
          "Résistance mécanique très élevée",
          "Surface lisse et anti-poussière",
          "Entretien simplifié",
          "Excellente longévité",
        ],
      },
      {
        title: "Finitions possibles",
        items: [
          "Lissé brut couleur béton",
          "Quartzé pour résistance accrue",
          "Teinté dans la masse ou en surface",
          "Joints de fractionnement réalisés au cordeau",
        ],
      },
    ],
  },
  {
    slug: "rampe-acces",
    group: "chapes",
    icon: Hammer,
    title: "Rampe d'accès garage",
    tagline: "Une rampe sûre, antidérapante, dimensionnée pour durer.",
    image:
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/dt2ww3ta_Snapchat-1826855908.jpg",
    gallery: [
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/dt2ww3ta_Snapchat-1826855908.jpg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/nxyb2xbs_VID-20260501-WA0094.jpg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/x1whibo8_IMG-20260501-WA0093.jpg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/qyq1qhop_Snapchat-1726506721.jpg",
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/nvg9gggy_20250620_164033.jpg",
    ],
    short:
      "Rampes béton rainurées pour l'accès aux garages et parkings — adhérence maximale et résistance aux passages répétés.",
    description:
      "Une rampe d'accès se travaille comme une pièce technique : pente régulière, surface rainurée à la balayette ou au peigne pour garantir l'adhérence des véhicules par tous les temps, intégration des avaloirs et grilles de récupération d'eau, raccord propre avec la voirie et le seuil du garage. Nous réalisons ces rampes en béton coulé sur place, dimensionnées au passage attendu (voitures, utilitaires, copropriétés).",
    sections: [
      {
        title: "Domaines d'application",
        items: [
          "Maisons individuelles avec garage en sous-sol",
          "Immeubles et copropriétés (parkings souterrains)",
          "Locaux professionnels et concessions",
          "Accès logistiques et zones de livraison",
        ],
      },
      {
        title: "Avantages",
        items: [
          "Surface rainurée antidérapante par tous temps",
          "Béton dimensionné au passage de véhicules",
          "Intégration des évacuations d'eau",
          "Raccord net avec la voirie et le seuil",
        ],
      },
      {
        title: "Mise en œuvre",
        items: [
          "Étude de la pente et des dimensions",
          "Coffrage et coulage du béton sur place",
          "Réalisation manuelle des rainures pour l'adhérence",
          "Pose des grilles et avaloirs si nécessaire",
        ],
      },
    ],
  },
  {
    slug: "isolation-thermique",
    group: "isolations",
    icon: Thermometer,
    title: "Isolation thermique",
    tagline: "Un sol bien isolé, une maison qui consomme moins.",
    image: null,
    short:
      "Pose de complexes isolants sous chape pour optimiser la performance énergétique de vos planchers.",
    description:
      "L'isolation thermique sous chape consiste à intercaler un complexe isolant (panneaux rigides type PSE, PUR, fibre de bois) entre le support et la chape. Elle réduit significativement les déperditions par le sol et participe à la performance globale de l'enveloppe du bâtiment, conformément aux exigences de la RE2020.",
    sections: [
      {
        title: "Domaines d'application",
        items: [
          "Constructions neuves RE2020",
          "Rénovation énergétique",
          "Sols sur terre-plein, vide sanitaire ou local non chauffé",
          "Maisons individuelles et logements collectifs",
        ],
      },
      {
        title: "Avantages",
        items: [
          "Réduction des ponts thermiques au sol",
          "Économies de chauffage",
          "Confort accru en hiver comme en été",
          "Valorisation du DPE",
        ],
      },
      {
        title: "Mise en œuvre",
        items: [
          "Préparation et nettoyage du support",
          "Pose des panneaux isolants jointifs",
          "Pose d'un film de désolidarisation",
          "Coulage de la chape par-dessus",
        ],
      },
    ],
  },
  {
    slug: "isolation-phonique",
    group: "isolations",
    icon: Volume2,
    title: "Isolation phonique",
    tagline: "Des intérieurs apaisés, conformes à la réglementation.",
    image: null,
    short:
      "Sous-couches résilientes performantes contre les bruits de chocs et de transmission entre étages.",
    description:
      "L'isolation phonique des sols repose sur la pose d'une sous-couche acoustique résiliente entre la dalle et la chape. Elle absorbe les vibrations et atténue significativement les bruits d'impact (chute d'objets, talons, déplacement de meubles), pour le confort des occupants et la conformité à la NRA.",
    sections: [
      {
        title: "Domaines d'application",
        items: [
          "Logements collectifs (immeubles, copropriétés)",
          "Hôtels, résidences, bâtiments tertiaires",
          "Crèches, écoles, salles de classe",
          "Maisons individuelles avec étages",
        ],
      },
      {
        title: "Avantages",
        items: [
          "Réduction efficace des bruits d'impact",
          "Conformité à la NRA",
          "Combinable avec une isolation thermique",
          "Confort acoustique au quotidien",
        ],
      },
      {
        title: "Mise en œuvre",
        items: [
          "Pose d'une sous-couche résiliente sur le support",
          "Remontée en périphérie pour désolidariser les murs",
          "Coulage d'une chape flottante",
          "Pose du revêtement final",
        ],
      },
    ],
  },
  {
    slug: "renovation-sols",
    group: "solutions",
    icon: Wrench,
    title: "Rénovation des sols",
    tagline: "Reprendre, niveler, remettre en état.",
    image: null,
    short:
      "Reprise et mise à niveau de planchers existants : ragréage, ravoirage, recouvrement de revêtements.",
    description:
      "En rénovation, l'état du support conditionne tout le chantier. Nous intervenons sur les planchers existants pour les diagnostiquer, les préparer et les remettre à niveau : ragréage des défauts de planéité, ravoirage pour intégrer les réseaux, recouvrement d'anciens revêtements non putrescibles.",
    sections: [
      {
        title: "Interventions",
        items: [
          "Diagnostic du support existant",
          "Ragréage de surface jusqu'à plusieurs centimètres",
          "Ravoirage pour passage de réseaux (plomberie, électricité)",
          "Recouvrement d'anciens carrelages ou bétons",
        ],
      },
      {
        title: "Avantages",
        items: [
          "Pas de démolition lourde",
          "Délais réduits",
          "Compatible avec planchers bois et béton",
          "Surface prête à recevoir un nouveau revêtement",
        ],
      },
    ],
  },
  {
    slug: "sechage-rapide",
    group: "solutions",
    icon: Timer,
    title: "Séchage rapide",
    tagline: "Quand le calendrier presse.",
    image: null,
    short:
      "Chapes à séchage accéléré pour raccourcir les délais avant la pose du revêtement.",
    description:
      "Les chapes à séchage rapide intègrent des adjuvants spécifiques permettant de réduire significativement le temps de séchage avant la pose du revêtement. Elles sont la solution idéale pour les chantiers à planning serré, les rénovations rapides ou les opérations de livraison urgente.",
    sections: [
      {
        title: "Domaines d'application",
        items: [
          "Chantiers à délais serrés",
          "Rénovations en site occupé",
          "Magasins et locaux commerciaux à livrer rapidement",
          "Opérations immobilières en phase finale",
        ],
      },
      {
        title: "Avantages",
        items: [
          "Reprise du chantier accélérée",
          "Délais maîtrisés",
          "Compatible avec la plupart des revêtements",
          "Performances mécaniques préservées",
        ],
      },
    ],
  },
  {
    slug: "sols-trafic",
    group: "solutions",
    icon: Truck,
    title: "Sols à trafic intense",
    tagline: "Des sols dimensionnés pour durer.",
    image: null,
    short:
      "Sols pour locaux soumis à de fortes sollicitations : ateliers, garages, entrepôts, parkings.",
    description:
      "Pour les locaux où circulent des véhicules, des engins de manutention ou un public nombreux, le sol doit être dimensionné en conséquence. Nous proposons des solutions techniques associant béton à haute résistance, durcisseur de surface et joints de fractionnement maîtrisés.",
    sections: [
      {
        title: "Domaines d'application",
        items: [
          "Entrepôts et plateformes logistiques",
          "Ateliers de production",
          "Garages, concessions automobiles",
          "Parkings souterrains et aériens",
        ],
      },
      {
        title: "Avantages",
        items: [
          "Forte résistance à l'usure et aux chocs",
          "Adapté au passage de chariots élévateurs et véhicules",
          "Surface lisse et facile à entretenir",
          "Longévité élevée même en usage intensif",
        ],
      },
    ],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
export const getServicesByGroup = (group) => SERVICES.filter((s) => s.group === group);
